import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCorners,
  defaultKeyboardCoordinateGetter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { ActionMenu, type ActionMenuItem } from './ActionMenu'
import { Button } from './Button'
import { MiniLoader } from './MiniLoader'
import { Pressable } from './Pressable'
import { useNls } from '../../nls/nlsProvider'
import { useIsMobile } from './useIsMobile'
import './StageBoard.css'

export interface StageBoardStage {
  id: string
  label: string
  count?: number
}

export interface StageBoardCanMoveResult {
  allowed: boolean
  reasonLabel?: string
}

export interface StageBoardProps<T> {
  stages: StageBoardStage[]
  items: Record<string, T[]>
  renderCard: (item: T, stageId: string) => ReactNode
  onMove: (itemId: string, toStage: string) => Promise<void>
  canMove?: (item: T, toStage: string) => StageBoardCanMoveResult
  onLoadMore?: (stageId: string) => void
  hasMore?: Record<string, boolean> | ((stageId: string) => boolean)
  loading?: Record<string, boolean> | ((stageId: string) => boolean)
  emptyLabel?: string | ((stageId: string) => string)
  getItemId?: (item: T) => string
  phoneMode?: boolean
  className?: string
  testId?: string
}

interface DraggableCardProps<T> {
  item: T
  itemId: string
  currentStageId: string
  stages: StageBoardStage[]
  renderCard: (item: T, stageId: string) => ReactNode
  onMoveItem: (itemId: string, toStage: string) => void
  canMove?: (item: T, toStage: string) => StageBoardCanMoveResult
  isPhone?: boolean
}

function StageBoardCard<T>({
  item,
  itemId,
  currentStageId,
  stages,
  renderCard,
  onMoveItem,
  canMove,
  isPhone = false,
}: DraggableCardProps<T>) {
  const { t } = useNls()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: itemId,
    data: { itemId, stageId: currentStageId },
    disabled: isPhone,
  })

  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 999,
      }
    : undefined

  const menuItems: ActionMenuItem[] = useMemo(() => {
    return stages
      .filter((s) => s.id !== currentStageId)
      .map((target) => {
        const check = canMove ? canMove(item, target.id) : { allowed: true }
        if (!check.allowed) {
          const reasonSuffix = check.reasonLabel ? ` (${check.reasonLabel})` : ''
          return {
            key: target.id,
            label: `${target.label}${reasonSuffix}`,
            title: check.reasonLabel,
            disabled: true,
            onSelect: () => undefined,
          }
        }
        return {
          key: target.id,
          label: target.label,
          disabled: false,
          onSelect: () => onMoveItem(itemId, target.id),
        }
      })
  }, [stages, currentStageId, canMove, item, itemId, onMoveItem])

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`stageBoardCardWrapper${isDragging ? ' isDragging' : ''}`}
      data-testid={`stageboard-card-${itemId}`}
      tabIndex={isPhone ? undefined : 0}
      {...(isPhone ? {} : attributes)}
      {...(isPhone ? {} : listeners)}
    >
      <div className="stageBoardCardMenuAnchor">
        <ActionMenu
          ariaLabel={t('NLSStageBoardMoveTo')}
          trigger="kebab"
          size="sm"
          items={menuItems}
          renderTrigger={({ toggle, anchorRef: setAnchor }) => (
            <Pressable
              ref={setAnchor}
              className="stageBoardCardMenuBtn"
              aria-label={t('NLSStageBoardMoveTo')}
              data-testid={`stageboard-menu-btn-${itemId}`}
              onClick={(e) => {
                e.stopPropagation()
                toggle()
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.stopPropagation()
                }
              }}
            >
              <MoreVertRoundedIcon fontSize="small" />
            </Pressable>
          )}
        />
      </div>
      <div className="stageBoardCardBody">{renderCard(item, currentStageId)}</div>
    </div>
  )
}

interface DroppableColumnProps {
  stage: StageBoardStage
  children: ReactNode
  count: number
}

function StageBoardColumnContainer({ stage, children, count }: DroppableColumnProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: stage.id,
    data: { stageId: stage.id },
  })

  return (
    <div
      ref={setNodeRef}
      className={`stageBoardColumn${isOver ? ' isOver' : ''}`}
      data-testid={`stageboard-column-${stage.id}`}
    >
      <header className="stageBoardColumnHeader">
        <div className="stageBoardColumnTitleWrap">
          <span className="stageBoardColumnLabel">{stage.label}</span>
          <span className="stageBoardColumnCount" data-testid={`stageboard-count-${stage.id}`}>
            {count}
          </span>
        </div>
      </header>
      <div className="stageBoardCardsList">{children}</div>
    </div>
  )
}

export function StageBoard<T>({
  stages,
  items,
  renderCard,
  onMove,
  canMove,
  onLoadMore,
  hasMore,
  loading,
  emptyLabel,
  getItemId = (item: T) => (item as { id?: string })?.id ?? '',
  phoneMode,
  className = '',
  testId = 'stageboard',
}: StageBoardProps<T>) {
  const { t } = useNls()
  const isMobileQuery = useIsMobile()
  const isPhone = phoneMode ?? isMobileQuery

  const [optimisticOverrides, setOptimisticOverrides] = useState<Record<string, string>>({})
  const [moveError, setMoveError] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState<string>('')
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const s of stages) {
      init[s.id] = true
    }
    return init
  })

  const dragStartStageRef = useRef<string | null>(null)
  const dragStartLeftRef = useRef<number | null>(null)

  // Map item IDs to their base (prop) stage
  const itemPropStageMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const [stageId, stageItems] of Object.entries(items)) {
      if (Array.isArray(stageItems)) {
        for (const item of stageItems) {
          const id = getItemId(item)
          if (id) map.set(id, stageId)
        }
      }
    }
    return map
  }, [items, getItemId])

  // Compute effective items per stage with optimistic overrides applied
  const effectiveStageItems = useMemo(() => {
    const result: Record<string, T[]> = {}
    for (const stage of stages) {
      result[stage.id] = []
    }

    for (const stage of stages) {
      const stageItems = items[stage.id] || []
      for (const item of stageItems) {
        const id = getItemId(item)
        const overrideStage = id ? optimisticOverrides[id] : undefined
        const effectiveStage = overrideStage || stage.id
        if (result[effectiveStage]) {
          result[effectiveStage].push(item)
        } else {
          result[effectiveStage] = [item]
        }
      }
    }
    return result
  }, [stages, items, getItemId, optimisticOverrides])

  const findStageOfItem = useCallback(
    (id: string): string => {
      if (optimisticOverrides[id]) return optimisticOverrides[id]
      return itemPropStageMap.get(id) || stages[0]?.id || ''
    },
    [optimisticOverrides, itemPropStageMap, stages],
  )

  const findItemById = useCallback(
    (id: string): T | undefined => {
      for (const stageList of Object.values(items)) {
        if (Array.isArray(stageList)) {
          const found = stageList.find((it) => getItemId(it) === id)
          if (found) return found
        }
      }
      return undefined
    },
    [items, getItemId],
  )

  const performMove = useCallback(
    async (itemId: string, targetStageId: string) => {
      const fromStageId = findStageOfItem(itemId)
      if (fromStageId === targetStageId) return

      const item = findItemById(itemId)
      if (item && canMove) {
        const check = canMove(item, targetStageId)
        if (!check.allowed) return
      }

      const toStageLabel = stages.find((s) => s.id === targetStageId)?.label || targetStageId
      setAnnouncement(t('NLSStageBoardAnnounceDrop', { id: itemId, stage: toStageLabel }))
      setMoveError(null)

      // Optimistic apply
      setOptimisticOverrides((prev) => ({ ...prev, [itemId]: targetStageId }))

      try {
        await onMove(itemId, targetStageId)
      } catch {
        // Revert on reject!
        setOptimisticOverrides((prev) => {
          const copy = { ...prev }
          delete copy[itemId]
          return copy
        })
        const fromStageLabel = stages.find((s) => s.id === fromStageId)?.label || fromStageId
        setAnnouncement(t('NLSStageBoardAnnounceCancel', { id: itemId, stage: fromStageLabel }))
        setMoveError(t('NLSStageBoardMoveError'))
      }
    },
    [findStageOfItem, findItemById, canMove, stages, t, onMove],
  )

  // Sensors for keyboard + pointer + touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: defaultKeyboardCoordinateGetter,
    }),
  )

  // Custom collision detection handling jsdom 0-rects gracefully
  const customCollisionDetection: CollisionDetection = useCallback(
    (args) => {
      // Check if droppables have zero dimensions (e.g. in test environment without layout engine)
      const isZeroRects =
        args.droppableContainers.length > 0 &&
        args.droppableContainers.every(
          (c) => !c.rect.current || (c.rect.current.width === 0 && c.rect.current.height === 0),
        )

      if (!isZeroRects) {
        const standard = closestCorners(args)
        if (standard && standard.length > 0) {
          return standard
        }
      }

      // Fallback for keyboard movement in test / zero-dimension rects
      const activeId = String(args.active.id)
      const currentStage = findStageOfItem(activeId)
      const stageIdx = stages.findIndex((s) => s.id === currentStage)
      if (stageIdx !== -1 && args.collisionRect) {
        if (dragStartLeftRef.current === null) {
          dragStartLeftRef.current = args.collisionRect.left
        }
        const deltaX = args.collisionRect.left - dragStartLeftRef.current
        if (deltaX > 10 && stageIdx < stages.length - 1) {
          return [{ id: stages[stageIdx + 1].id }]
        }
        if (deltaX < -10 && stageIdx > 0) {
          return [{ id: stages[stageIdx - 1].id }]
        }
        return [{ id: stages[stageIdx].id }]
      }
      return []
    },
    [findStageOfItem, stages],
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeId = String(event.active.id)
      const stageId = findStageOfItem(activeId)
      dragStartStageRef.current = stageId
      const stageLabel = stages.find((s) => s.id === stageId)?.label || stageId
      setAnnouncement(t('NLSStageBoardAnnouncePick', { id: activeId, stage: stageLabel }))
    },
    [findStageOfItem, stages, t],
  )

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event
      if (!over) return
      const overStageLabel = stages.find((s) => s.id === String(over.id))?.label || String(over.id)
      setAnnouncement(t('NLSStageBoardAnnounceOver', { id: String(active.id), stage: overStageLabel }))
    },
    [stages, t],
  )

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      dragStartStageRef.current = null
      dragStartLeftRef.current = null
      if (!over) return

      const itemId = String(active.id)
      const targetStageId = String(over.id)
      await performMove(itemId, targetStageId)
    },
    [performMove],
  )

  const handleDragCancel = useCallback(() => {
    const stageId = dragStartStageRef.current
    dragStartStageRef.current = null
    dragStartLeftRef.current = null
    if (stageId) {
      const stageLabel = stages.find((s) => s.id === stageId)?.label || stageId
      setAnnouncement(t('NLSStageBoardAnnounceCancel', { id: '', stage: stageLabel }))
    }
  }, [stages, t])

  const isStageLoading = useCallback(
    (stageId: string) => {
      if (typeof loading === 'function') return loading(stageId)
      return loading ? Boolean(loading[stageId]) : false
    },
    [loading],
  )

  const stageHasMore = useCallback(
    (stageId: string) => {
      if (typeof hasMore === 'function') return hasMore(stageId)
      return hasMore ? Boolean(hasMore[stageId]) : false
    },
    [hasMore],
  )

  const getEmptyLabel = useCallback(
    (stageId: string) => {
      if (typeof emptyLabel === 'function') return emptyLabel(stageId)
      return emptyLabel || t('NLSStageBoardEmpty')
    },
    [emptyLabel, t],
  )

  const toggleSection = (stageId: string) => {
    setExpandedSections((prev) => ({ ...prev, [stageId]: !prev[stageId] }))
  }

  return (
    <div className={`stageBoardRoot ${className}`.trim()} data-testid={testId}>
      {/* Live announcement region */}
      <div
        className="stageBoardSrOnly"
        role="status"
        aria-live="polite"
        data-testid="stageboard-live-announcement"
      >
        {announcement}
      </div>

      {moveError && (
        <div className="stageBoardInlineError" role="alert" data-testid="stageboard-error">
          {moveError}
        </div>
      )}

      {isPhone ? (
        /* Phone collapsible list under 600px */
        <div className="stageBoardPhoneList" data-testid="stageboard-phone-list">
          {stages.map((stage) => {
            const stageItems = effectiveStageItems[stage.id] || []
            const isExpanded = Boolean(expandedSections[stage.id])
            const isLoading = isStageLoading(stage.id)
            const canLoadMore = stageHasMore(stage.id)

            return (
              <section
                key={stage.id}
                className="stageBoardSection"
                data-testid={`stageboard-section-${stage.id}`}
              >
                <Pressable
                  className="stageBoardSectionHeader"
                  onClick={() => toggleSection(stage.id)}
                  aria-expanded={isExpanded}
                  data-testid={`stageboard-toggle-${stage.id}`}
                >
                  <div className="stageBoardColumnTitleWrap">
                    <span className="stageBoardColumnLabel">{stage.label}</span>
                    <span
                      className="stageBoardColumnCount"
                      data-testid={`stageboard-phone-count-${stage.id}`}
                    >
                      {stage.count ?? stageItems.length}
                    </span>
                  </div>
                  <span
                    className={`stageBoardSectionChevron${isExpanded ? ' isExpanded' : ''}`}
                    aria-hidden="true"
                  >
                    <ExpandMoreRoundedIcon fontSize="small" />
                  </span>
                </Pressable>

                {isExpanded && (
                  <div className="stageBoardSectionBody">
                    {stageItems.length === 0 && !isLoading && (
                      <div className="stageBoardEmptyMessage">{getEmptyLabel(stage.id)}</div>
                    )}
                    {stageItems.map((item) => {
                      const id = getItemId(item)
                      return (
                        <StageBoardCard
                          key={id}
                          item={item}
                          itemId={id}
                          currentStageId={stage.id}
                          stages={stages}
                          renderCard={renderCard}
                          onMoveItem={performMove}
                          canMove={canMove}
                          isPhone={true}
                        />
                      )
                    })}
                    {isLoading && (
                      <div className="stageBoardLoadMoreWrap">
                        <MiniLoader size="sm" />
                      </div>
                    )}
                    {canLoadMore && !isLoading && onLoadMore && (
                      <div className="stageBoardLoadMoreWrap">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onLoadMore(stage.id)}
                          data-testid={`stageboard-load-more-${stage.id}`}
                        >
                          {t('NLSStageBoardLoadMore')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )
          })}
        </div>
      ) : (
        /* Desktop horizontal columns */
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="stageBoardColumnsTrack">
            {stages.map((stage) => {
              const stageItems = effectiveStageItems[stage.id] || []
              const isLoading = isStageLoading(stage.id)
              const canLoadMore = stageHasMore(stage.id)

              return (
                <StageBoardColumnContainer
                  key={stage.id}
                  stage={stage}
                  count={stage.count ?? stageItems.length}
                >
                  {stageItems.map((item) => {
                    const id = getItemId(item)
                    return (
                      <StageBoardCard
                        key={id}
                        item={item}
                        itemId={id}
                        currentStageId={stage.id}
                        stages={stages}
                        renderCard={renderCard}
                        onMoveItem={performMove}
                        canMove={canMove}
                        isPhone={false}
                      />
                    )
                  })}
                  {stageItems.length === 0 && !isLoading && (
                    <div className="stageBoardEmptyMessage">{getEmptyLabel(stage.id)}</div>
                  )}
                  {isLoading && (
                    <div className="stageBoardLoadMoreWrap">
                      <MiniLoader size="sm" />
                    </div>
                  )}
                  {canLoadMore && !isLoading && onLoadMore && (
                    <div className="stageBoardLoadMoreWrap">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onLoadMore(stage.id)}
                        data-testid={`stageboard-load-more-${stage.id}`}
                      >
                        {t('NLSStageBoardLoadMore')}
                      </Button>
                    </div>
                  )}
                </StageBoardColumnContainer>
              )
            })}
          </div>
        </DndContext>
      )}
    </div>
  )
}
